import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { FileText, Check, Loader2, ArrowLeft } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Base monthly prices (INR). Quarterly = 3× × 0.9 (10% off). Yearly = 12× × 0.8 (20% off).
const BASE_PRICES = { pro: 99, plus: 250 };

const CYCLES = [
  { id: 'monthly',   label: 'Monthly',   months: 1,  discount: 0,   badge: null },
  { id: 'quarterly', label: 'Quarterly', months: 3,  discount: 0.1, badge: 'SAVE 10%' },
  { id: 'yearly',    label: 'Yearly',    months: 12, discount: 0.2, badge: 'SAVE 20%' },
];

const PLAN_META = {
  pro: {
    name: 'Pro',
    description: 'Export & share your resumes',
    features: ['Download as PDF (LaTeX quality)', 'Download as DOCX', 'All 3 premium templates', 'Shareable public resume link', 'AI bullet point generator', 'AI summary generator'],
    accent: 'border-slate-900 dark:border-slate-500 shadow-[6px_6px_0px_#0F172A] dark:shadow-[6px_6px_0px_#38bdf8]',
    btn: 'bg-[#FB923C] hover:bg-orange-300 text-slate-900 shadow-[4px_4px_0px_#0F172A]',
    themeColor: '#FB923C',
    badge: 'POPULAR',
    badgeColor: 'bg-[#FB923C] text-slate-900',
  },
  plus: {
    name: 'Plus',
    description: 'Full ATS optimization engine',
    features: ['Everything in Pro', 'ATS Optimization Engine', 'AI resume rewriting for job roles', 'Before/After ATS scoring', 'Missing keyword detection', 'Resume diff & change tracking'],
    accent: 'border-amber-400 shadow-[6px_6px_0px_#F59E0B]',
    btn: 'bg-amber-400 hover:bg-amber-300 text-slate-900 shadow-[4px_4px_0px_#0F172A]',
    themeColor: '#F59E0B',
    badge: 'BEST VALUE',
    badgeColor: 'bg-amber-400 text-slate-900',
  },
};

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) { resolve(true); return; }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function computePrice(tier, cycle) {
  const monthly = BASE_PRICES[tier];
  const total = Math.floor(monthly * cycle.months * (1 - cycle.discount));
  const perMonth = Math.floor(total / cycle.months);
  return { total, perMonth, original: monthly * cycle.months };
}

function CycleToggle({ cycle, onChange }) {
  return (
    <div className="inline-flex bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-500 p-1" data-testid="cycle-toggle">
      {CYCLES.map((c) => (
        <button
          key={c.id}
          data-testid={`cycle-${c.id}-btn`}
          onClick={() => onChange(c.id)}
          className={`relative px-5 py-2 text-sm font-bold transition-all ${cycle === c.id ? 'bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
        >
          {c.label}
          {c.badge && (
            <span className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[10px] font-black bg-[#FB923C] text-slate-900 rounded-sm whitespace-nowrap">
              {c.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function PlanCard({ tier, cycle, isCurrent, paying, onSubscribe }) {
  const meta = PLAN_META[tier];
  const price = computePrice(tier, cycle);
  const showStrike = cycle.discount > 0;
  const planId = `${tier}_${cycle.id}`;

  return (
    <div className={`bg-white dark:bg-slate-800 border-2 ${meta.accent} p-6 md:p-8 relative`} data-testid={`plan-card-${planId}`}>
      <div className={`absolute -top-3 left-4 px-3 py-0.5 text-xs font-bold ${meta.badgeColor}`}>{meta.badge}</div>

      <div className="flex items-baseline gap-2 mb-1 mt-2">
        <span className="text-5xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid={`plan-price-${planId}`}>
          ₹{price.total}
        </span>
        <span className="text-base text-slate-500 dark:text-slate-400">/{cycle.label.toLowerCase()}</span>
      </div>
      {showStrike ? (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm text-slate-400 line-through">₹{price.original}</span>
          <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{Math.round(cycle.discount * 100)}% OFF</span>
        </div>
      ) : (
        <p className="text-xs text-slate-400 mb-2">Billed monthly · cancel anytime</p>
      )}
      {cycle.months > 1 && (
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">₹{price.perMonth}/month equivalent</p>
      )}

      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{meta.name}</h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{meta.description}</p>

      <div className="space-y-3 mb-8">
        {meta.features.map((f) => (
          <div key={f} className="flex items-start gap-2.5">
            <div className="bg-[#FFEDD5] dark:bg-orange-900 p-0.5 mt-0.5"><Check className="w-3.5 h-3.5 text-[#EA580C]" strokeWidth={2.5} /></div>
            <span className="text-sm text-slate-700 dark:text-slate-300">{f}</span>
          </div>
        ))}
      </div>

      {isCurrent ? (
        <div className="bg-[#FFEDD5] dark:bg-orange-900 border border-[#FB923C] px-4 py-3 text-center">
          <span className="text-sm font-bold text-slate-900 dark:text-orange-100 flex items-center justify-center gap-2"><Check className="w-4 h-4" /> Current Plan</span>
        </div>
      ) : (
        <Button
          data-testid={`subscribe-${planId}-btn`}
          onClick={() => onSubscribe(planId)}
          disabled={paying}
          className={`w-full font-bold rounded-none px-6 py-3 transition-all hover:-translate-y-1 ${meta.btn}`}
        >
          {paying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Get {meta.name}
        </Button>
      )}
    </div>
  );
}

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh: refreshSubscription } = useSubscription();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [cycleId, setCycleId] = useState('monthly');

  const cycle = useMemo(() => CYCLES.find((c) => c.id === cycleId), [cycleId]);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/subscription/status`, { withCredentials: true });
      setSubscription(data.subscription);
    } catch (error) {
      toast.error('Could not check subscription status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSubscribe = useCallback(async (planId) => {
    setPaying(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) { toast.error('Failed to load payment gateway.'); setPaying(false); return; }

      const [tier, cyc] = planId.split('_');
      const meta = PLAN_META[tier];
      const { data: order } = await axios.post(`${API_URL}/api/subscription/create-order`, { plan: planId }, { withCredentials: true });

      const options = {
        key: order.key_id, amount: order.amount, currency: order.currency,
        name: 'Aarova AI', description: `${meta.name} ${cyc.charAt(0).toUpperCase() + cyc.slice(1)} — ₹${order.amount / 100}`,
        order_id: order.order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API_URL}/api/subscription/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }, { withCredentials: true });
            toast.success(`${meta.name} activated!`);
            setSubscription({ plan: planId, status: 'active' });
            await refreshSubscription();
          } catch (error) {
            toast.error('Payment verification failed.');
          }
          setPaying(false);
        },
        prefill: { name: user?.name || '', email: user?.email || '' },
        theme: { color: meta.themeColor },
        modal: { ondismiss: () => setPaying(false) }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => { toast.error('Payment failed.'); setPaying(false); });
      rzp.open();
    } catch (error) {
      toast.error('Could not initiate payment.'); setPaying(false);
    }
  }, [user, refreshSubscription]);

  const isActive = subscription?.status === 'active';
  const activePlan = subscription?.plan;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <FileText className="w-8 h-8 text-[#FB923C]" strokeWidth={2} />
            <span className="text-2xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Aarova AI</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button data-testid="back-dashboard-btn" onClick={() => navigate('/dashboard')} className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-none px-4 py-2 text-sm">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        <div className="text-center mb-10">
          <h1 className="text-5xl md:text-6xl tracking-tighter font-black text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="subscription-heading">
            Choose your plan
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Unlock AI-powered features to land your dream job faster
          </p>
          <div className="flex justify-center">
            <CycleToggle cycle={cycleId} onChange={setCycleId} />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FB923C]" /></div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {['pro', 'plus'].map((tier) => (
              <PlanCard
                key={tier}
                tier={tier}
                cycle={cycle}
                isCurrent={isActive && activePlan === `${tier}_${cycleId}`}
                paying={paying}
                onSubscribe={handleSubscribe}
              />
            ))}
          </div>
        )}

        <p className="text-xs text-slate-400 text-center mt-8">Secure payment powered by Razorpay. Cancel anytime.</p>
      </main>
    </div>
  );
}
